<template>
  <div>
    <FusionBaseDialog
      @close="close"
      :open="open"
      :error="dialogError"
      :urlError="dialogUrlError"
      :urlErrorMsg="dialogUrlErrorMsg"
      :max-width="800"
      :max-height="'90%'"
      :persistentOverlay="true"
      padded
      customCss="register-single-device"
    >
      <template v-slot:header>
        <v-card-item prepend-icon="mdi-map-marker-plus">
          <v-card-title> {{ $t('activateDevice.title') }} </v-card-title>
        </v-card-item>
        <v-card-item class="pt-0 mt-n2">
          <v-card-subtitle class="text-wrap">{{ $t('activateDevice.subtitle') }}</v-card-subtitle>
        </v-card-item>
      </template>
      <v-form ref="form" data-cy="registerDeviceCard">
        <v-row no-gutters>
          <v-col cols="12">
            <v-card flat>
              <DeviceAttributeTextarea
                v-model="deviceName"
                :label="$t('activateDevice.deviceName')"
                :rules="[rules.required]"
                required
                :cyTag="'deviceName'"
              />
              <DeviceAttributeTextarea
                v-model="deviceLocationName"
                :label="$t('activateDevice.deviceLocationName')"
                :cyTag="'deviceLocationName'"
              />
            </v-card>
          </v-col>
          <v-col cols="12" sm="6">
            <v-card flat :title="$t('activateDevice.details')">
              <v-select
                ref="deviceTypeRef"
                v-model="deviceType"
                :items="deviceTypeItems"
                :disabled="prefilled"
                :rules="[rules.required, devicesRemainingValid]"
                :label="$t('activateDevice.deviceType')"
                @update:model-value="deviceTypeChanged()"
                data-cy="deviceTypeSelect"
                class="text-field-required"
              ></v-select>
              <!--
                Smart Bridge (sbd1) covers both SoftBridge/Smart Bridge (install, 26 characters)
                and HardBridge/Smart Bridge (gateway, 13 characters), so the counter and maxlength
                are disabled for it. SoftBridge/Smart Bridge (install) will likely be removed in the
                future; at that point restore these using the HardBridge/Smart Bridge (gateway)
                serial length.
              -->
              <v-text-field
                :disabled="prefilled || !deviceType"
                v-model="idSerialNumber"
                :label="$t('activateDevice.idSerialNumber')"
                :hint="serialNumberHintText"
                persistent-hint
                :counter="isSmartBridge ? undefined : expectedSerialLength"
                :maxlength="isSmartBridge ? undefined : expectedSerialLength"
                :rules="[
                  rules.required,
                  serialHasValidCharacters,
                  rules.serialLength,
                  serialHasValidModelNumber
                ]"
                @keypress="preventNonNumbers($event)"
                data-cy="deviceSerial"
                class="text-field-required"
                ref="idSerialNumberRef"
              ></v-text-field>
              <v-switch
                v-model="isPublic"
                :label="$t('activateDevice.isPublic')"
                color="primary"
                :hint="$t('activateDevice.isPublicDescription')"
                persistent-hint
                :disabled="isSmartBridge"
                data-cy="public"
              ></v-switch>
              <v-switch
                v-if="!isSmartBridge"
                v-model="indoorSensor"
                :disabled="isIndoorOnly"
                color="primary"
                :hint="indoorSensorHint"
                :persistent-hint="isIndoorOnly"
                data-cy="isIndoor"
              >
                <template v-slot:label>
                  {{ $t('activateDevice.indoorSensor') }}
                  <div class="device-type-indicator">
                    <img v-if="indoorSensor" :src="indoorPinSrc" alt="Indoor Device Pin" />
                    <img v-if="!indoorSensor" :src="outdoorPinSrc" alt="Outdoor Device Pin" />
                  </div>
                </template>
              </v-switch>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6">
            <v-card v-if="showSubTierSelect" flat :title="$t('activateDevice.subscription')">
              <v-select
                v-model="subscriptionTier"
                :items="subscriptionTierItems"
                :label="$t('activateDevice.subscriptionTier')"
                :hint="subTierHint"
                persistent-hint
                :rules="[rules.required]"
                data-cy="subscriptionTierSelect"
                class="text-field-required"
              ></v-select>
            </v-card>
            <v-card :disabled="isSmartBridge" flat :title="$t('activateDevice.location')">
              <v-btn
                :loading="gettingLocation"
                @click="useBrowserLocation"
                variant="tonal"
                color="primary"
                :disabled="locationError"
                class="my-2"
                data-cy="useBrowserLocation"
              >
                {{ $t('activateDevice.useMyLocation') }}
              </v-btn>
              <div class="hint-text text-error opacity-100">{{ locationErrorMessage }}</div>
              <div class="hint-text mb-4">
                {{ $t('activateDevice.useMyLocationHint') }}
              </div>
              <v-text-field
                v-model="coords.latitude"
                :label="$t('activateDevice.latitude')"
                min="-90"
                max="90"
                maxlength="12"
                :rules="[rules.latitude]"
                @keypress="preventNonCoords($event)"
                data-cy="registerLatitude"
                class="text-field-required"
                ref="latRef"
              ></v-text-field>
              <v-text-field
                :label="$t('activateDevice.longitude')"
                persistent-hint
                v-model="coords.longitude"
                min="-180"
                max="180"
                maxlength="13"
                :rules="[rules.longitude]"
                @keypress="preventNonCoords($event)"
                data-cy="registerLongitude"
                class="text-field-required"
                ref="lngRef"
              ></v-text-field>
              <div class="hint-text">
                {{ $t('activateDevice.coordinateHint') }}
                <a
                  :href="$t('activateDevice.coordinateHintLink')"
                  class="hint-text-link"
                  target="_blank"
                  >{{ $t('activateDevice.coordinateHintLink') }}</a
                >
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
      <template v-slot:actions>
        <v-btn
          @click="close"
          variant="outlined"
          class="text-black"
          :disabled="registering"
          data-cy="registerClose"
          >{{ $t('buttons.close') }}</v-btn
        >
        <v-spacer></v-spacer>
        <v-btn
          @click="save"
          class="bg-primary text-white save-button"
          :disabled="!fieldsValid || registering"
          :loading="registering"
          prepend-icon=""
          append-icon="mdi-chevron-right"
          data-cy="registerDoneButton"
          variant="text"
        >
          {{ $t('buttons.done') }}
        </v-btn>
      </template>
    </FusionBaseDialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { VForm, VSelect, VTextField } from 'vuetify/components'
import { useLocationGetter, locationCoordsRegex } from '@/services/location'
import { serialHasValidCharacters, serialHasValidModelNumber } from '@/services/device-serial'
import { DEVICE_DEFAULT_CONFIG } from '@/services/device-config'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS, DEVICE_ACTIONS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import { SUBSCRIPTIONS_GETTERS, SUBSCRIPTIONS_ACTIONS } from '@/store/subscriptions'
import { deviceModels, subscriptionTypes, subscriptionTiers } from '@/store/subscriptions/utils'
import { MAP_GETTERS, MAP_ACTIONS } from '@/store/map'
import type { LocationCoords } from '@/store/location'
import type { SetDevicePayload } from '@/store/devices'
import type { RegisterDeviceRequest, StateItemPoint, UpdateDeviceRequest } from '@/types/devices'
import DeviceAttributeTextarea from '../deviceSettings/DeviceAttributeTextarea.vue'
import { formatRegisterError } from './multipleDevices/helper'

const LINK_CUSTOMER = 'Customer Support.'
const MANAGE_DEVICES_ROUTE_NAME = 'manage-devices'
const TSILINK_PLUS = subscriptionTiers.TSILINK_PLUS

interface DeviceTypeItem {
  title: string
  value: string
}

export default defineComponent({
  name: 'RegisterDialog',
  components: {
    DeviceAttributeTextarea
  },
  props: {
    open: { type: Boolean, default: false },
    deviceSerial: { type: String, default: '' },
    deviceModel: { type: String, default: '' },
    deviceId: { type: String, default: '' }
  },
  emits: ['close', 'deviceCreated'],
  setup() {
    const { getGeolocation } = useLocationGetter()
    return { getGeolocation }
  },
  data() {
    return {
      devicesRemaining: null as number | null,
      gettingLocation: false,
      locationError: false,
      locationErrorMessage: '',
      registerErrorMessage: '',
      registerError: false,
      urlError: '',
      urlErrorMsg: '',
      requiredError: false,
      registering: false,
      prefilled: false,
      deviceType: null as string | null,
      idSerialNumber: '',
      deviceName: '',
      deviceLocationName: '',
      isPublic: false,
      indoorSensor: false,
      coords: {
        latitude: '',
        longitude: ''
      },
      subscriptionTier: null as string | null,
      subscriptionTierItems: [
        {
          title: this.$t('activateDevice.subscriptionTierOptions.basic'),
          value: 'basic'
        },
        {
          title: this.$t('activateDevice.subscriptionTierOptions.premium'),
          value: 'premium'
        }
      ]
    }
  },
  computed: {
    rules() {
      return {
        required: (value: unknown): boolean | string => !!value || this.$t('validation.required'),
        serialLength: (value: string): boolean | string => {
          // Smart Bridge (sbd1) covers both SoftBridge/Smart Bridge (install, 26 characters) and
          // HardBridge/Smart Bridge (gateway, 13 characters), so length is not validated for it.
          // SoftBridge/Smart Bridge (install) will likely be removed in the future; at that point
          // restore this check using the HardBridge/Smart Bridge (gateway) serial length.
          if (this.isSmartBridge) {
            return true
          }
          return value.length === this.expectedSerialLength || this.$t('validation.invalidLength')
        },
        latitude: (value: string | number): boolean | string => {
          return (
            locationCoordsRegex.latitude.test(String(value)) || this.$t('validation.invalidEntry')
          )
        },
        longitude: (value: string | number): boolean | string => {
          return (
            locationCoordsRegex.longitude.test(String(value)) || this.$t('validation.invalidEntry')
          )
        }
      }
    },
    fieldsValid(): boolean {
      if (this.isSmartBridge) {
        if (
          this.rules.required(this.idSerialNumber) !== true ||
          this.serialHasValidCharacters !== true ||
          this.serialHasValidModelNumber !== true ||
          this.rules.required(this.deviceName) !== true ||
          this.rules.serialLength(this.idSerialNumber) !== true
        ) {
          return false
        }
      } else {
        const lng = parseFloat(this.coords.longitude)
        const lat = parseFloat(this.coords.latitude)
        const subTierValid = this.showSubTierSelect
          ? this.rules.required(this.subscriptionTier) === true
          : true
        if (
          (this.devicesRemaining !== null && this.devicesRemaining === 0) ||
          this.rules.required(this.deviceName) !== true ||
          this.rules.required(this.idSerialNumber) !== true ||
          this.serialHasValidCharacters !== true ||
          this.serialHasValidModelNumber !== true ||
          this.rules.serialLength(this.idSerialNumber) !== true ||
          this.rules.required(lng) !== true ||
          this.rules.longitude(lng) !== true ||
          this.rules.required(lat) !== true ||
          this.rules.latitude(lat) !== true ||
          !subTierValid
        ) {
          return false
        }
      }
      return true
    },
    dialogError(): string {
      if (this.registerError) {
        return this.registerErrorMessage
      } else if (this.requiredError) {
        return this.$t('activateDevice.validationRequiredError')
      }
      return this.$t('validation.requiredHelp')
    },
    dialogUrlError(): string {
      return this.urlError
    },
    dialogUrlErrorMsg(): string {
      return this.urlErrorMsg
    },
    isSmartBridge(): boolean {
      return this.modelNumber === deviceModels.SMART_BRIDGE
    },
    hasSmartBridgeSub(): boolean {
      return this.$store.getters[
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_GENERIC_TSI_LINK}`
      ].some((sub: { id: string }) => sub.id === TSILINK_PLUS)
    },
    modelNumber(): string {
      const modelNamesMap: Record<string, string> =
        this.$store.getters[
          `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_NAMES_MAP}`
        ]
      return (
        Object.keys(modelNamesMap).find(
          (modelNumber) => modelNamesMap[modelNumber] === this.deviceType
        ) || ''
      )
    },
    devId(): string | undefined {
      if (this.deviceId) {
        return this.deviceId
      } else if (this.idSerialNumber) {
        return this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_DEVICE_ID_BY_DEVICE_SERIAL}`
        ](this.idSerialNumber)
      }
      return undefined
    },
    devicesRemainingValid(): boolean | string {
      if (this.devicesRemaining === null) {
        return true
      }
      if (this.isSmartBridge) {
        return this.hasSmartBridgeSub || this.$t('activateDevice.noActiveSmartBridgeSubFound')
      } else {
        return (
          this.devicesRemaining > 0 ||
          this.$t('activateDevice.noDeviceAllowances', {
            modelName: this.deviceType
          })
        )
      }
    },
    serialHasValidCharacters(): boolean | string {
      return (
        serialHasValidCharacters(this.modelNumber, this.idSerialNumber) ||
        this.$t('validation.invalidCharacters')
      )
    },
    serialHasValidModelNumber(): boolean | string {
      return (
        serialHasValidModelNumber(this.modelNumber, this.idSerialNumber) ||
        this.$t('validation.invalidEntry')
      )
    },
    serialNumberHintText(): string {
      if (!this.deviceType) {
        return this.$t('activateDevice.idSerialNumberDisabled')
      }
      // The Smart Bridge hint describes the SoftBridge/Smart Bridge (install) system tray, which
      // does not apply to HardBridge/Smart Bridge (gateway) — both share the sbd1 model.
      // SoftBridge/Smart Bridge (install) will likely be removed in the future; at that point
      // restore hint messaging for the HardBridge/Smart Bridge (gateway).
      // return this.isSmartBridge
      //   ? this.$t('activateDevice.idSerialNumberHintSB')
      //   : this.$t('activateDevice.idSerialNumberHint')
      return this.isSmartBridge ? '' : this.$t('activateDevice.idSerialNumberHint')
    },
    isIndoorOnly(): boolean {
      return (
        !!this.modelNumber &&
        this.$store.getters[`${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_INDOOR_ONLY}`](
          this.modelNumber
        )
      )
    },
    modelNameKey(): string {
      return this.deviceType ? this.deviceType.toLowerCase().replace(/ /g, '_') : 'bluesky'
    },
    indoorSensorHint(): string {
      if (this.isIndoorOnly) {
        return this.$t('activateDevice.isIndoorOnlyHint', {
          modelName: this.deviceType
        })
      }
      return ''
    },
    indoorPinSrc(): string {
      return this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_PIN_PATH}`
      ](this.modelNumber, 'indoor')
    },
    outdoorPinSrc(): string {
      return this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_PIN_PATH}`
      ](this.modelNumber, 'outdoor')
    },
    canAddToBasicSub(): boolean {
      return this.$store.getters[
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.CAN_ADD_DEVICE_TO_BASIC_BY_MODEL}`
      ](this.modelNumber)
    },
    canAddToPremiumSub(): boolean {
      return this.$store.getters[
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.CAN_ADD_DEVICE_TO_PREMIUM_BY_MODEL}`
      ](this.modelNumber)
    },
    hasDataServices(): boolean {
      return !!this.$store.getters[
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_DEVICE_DATA_SERVICES_BY_DEVICE_SERIAL}`
      ](this.idSerialNumber)
    },
    showSubTierSelect(): boolean {
      return this.canAddToBasicSub && this.canAddToPremiumSub
    },
    subTierHint(): string {
      return this.deviceType ? this.$t(`activateDevice.subscriptionTierHint${this.deviceType}`) : ''
    },
    expectedSerialLength(): number {
      return (
        this.modelNumber &&
        this.$store.getters[
          `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SERIAL_LENGTH}`
        ](this.modelNumber)
      )
    },
    deviceTypeItems(): DeviceTypeItem[] {
      const models: string[] =
        this.$store.getters[`${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODELS}`]
      const modelItems: DeviceTypeItem[] = []
      models.forEach((model) => {
        if (this.showRegistration(model)) {
          const modelKey: string =
            this.$store.getters[
              `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_KEY}`
            ](model)
          modelItems.push({
            title: this.$t(`map.mapSettings.deviceModelNames.${modelKey}`),
            value:
              this.$store.getters[
                `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_NAME}`
              ](model)
          })
        }
      })
      return modelItems
    }
  },
  watch: {
    isIndoorOnly(newVal: boolean) {
      this.indoorSensor = newVal === true
    },
    deviceType(newVal: string, oldVal: string) {
      if (newVal !== oldVal) {
        this.subscriptionTier = null
      }
    },
    open() {
      this.idSerialNumber = this.deviceSerial
      this.deviceType = this.deviceModel || null
      // Only check qty remaining if device type is pre filled
      if (this.deviceType && this.idSerialNumber) {
        this.deviceTypeChanged()
        this.prefilled = true
      }
      // Make it possible to prefill name, locationName and coords when adding data services device to website
      const name = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_NAME_BY_DEVICE_ID}`
      ](this.deviceId)
      if (name) {
        this.deviceName = name
      }
      const locationName = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_LOCATION_NAME_BY_DEVICE_ID}`
      ](this.deviceId)
      if (locationName) {
        this.deviceLocationName = locationName
      }
      const coords = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_COORDS_BY_DEVICE_ID}`
      ](this.devId)
      if (coords && coords.lat && coords.lng) {
        this.coords.latitude = coords.lat
        this.coords.longitude = coords.lng
      }
    }
  },
  methods: {
    showRegistration(model: string): boolean {
      const showRegistration =
        this.$store.getters[
          `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SHOW_REGISTRATION}`
        ](model)
      // If the showRegistration flag isn't available (Devices API deployment hasn't been done or has been rolled back),
      // default to true - otherwise the registration dropdown could be empty
      return showRegistration === undefined ? true : showRegistration
    },
    resetDeviceTypeRelatedFormValidations(): void {
      if (!this.idSerialNumber) {
        ;(this.$refs.idSerialNumberRef as VTextField).resetValidation()
      }
      if (this.isSmartBridge) {
        ;(this.$refs.latRef as VTextField).reset()
        ;(this.$refs.lngRef as VTextField).reset()
      }
    },
    preventNonNumbers(event: KeyboardEvent): void {
      // if 1st character of the model is a number then prevent non numbers
      if (!isNaN(Number(this.modelNumber.charAt(0)))) {
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab']
        if (allowedKeys.includes(event.key)) return
        if (!/^\d$/.test(event.key)) {
          event.preventDefault()
        }
      }
    },
    sanitizeUrl(url: string): string {
      if (url !== ' ') {
        return url.replace(/[^-A-Za-z0-9+&@#/%?=~_|!:,.;()]/g, '').toLowerCase()
      } else {
        return url
      }
    },
    async deviceTypeChanged(): Promise<void> {
      if (this.modelNumber) {
        this.devicesRemaining = null
        this.resetDeviceTypeRelatedFormValidations()
        const qtyRemaining = await this.$store.getters[
          `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_QTY_REMAINING}`
        ](this.modelNumber)
        this.devicesRemaining = qtyRemaining
        await this.$nextTick()
        ;(this.$refs.deviceTypeRef as VSelect)?.validate()
      }
    },
    setCoords(coords: LocationCoords | null | false): void {
      if (coords) {
        if (coords.lat && coords.lng) {
          this.coords.latitude = `${coords.lat.toFixed(8)}`
          this.coords.longitude = `${coords.lng.toFixed(8)}`
        } else {
          this.locationError = true
          this.locationErrorMessage = this.$t('activateDevice.locationNotAvailable')
        }
      } else {
        this.locationError = true
        this.locationErrorMessage = this.$t('activateDevice.locationDenied')
      }
      this.gettingLocation = false
    },
    useBrowserLocation(): void {
      this.gettingLocation = true

      this.getGeolocation((coords) => {
        this.setCoords(coords)
      })
    },
    preventNonCoords(event: KeyboardEvent) {
      // NOTE: may need to modify this to prevent input like "--4.323" or "0100..43" etc
      const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', '-', '.']
      if (allowedKeys.includes(event.key)) return
      if (!/^\d$/.test(event.key)) {
        event.preventDefault()
      }
    },
    updateMap(deviceId: string, deviceModel: string): void {
      // Update map settings to show device model if not set to visible
      const visibleDeviceModels: string[] =
        this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_DEVICE_MODELS}`] || []
      if (!visibleDeviceModels.includes(deviceModel)) {
        visibleDeviceModels.push(deviceModel)
        this.$store.dispatch(`${NAMESPACES.MAP}/${MAP_ACTIONS.UPDATE_SETTINGS}`, {
          deviceModels: visibleDeviceModels
        })
      }
      // pass along device ID so that moving mode can be enabled
      this.$emit('deviceCreated', deviceId)
    },
    async updateDeviceConfig(deviceId: string): Promise<void> {
      await this.$api.postDeviceConfig(DEVICE_DEFAULT_CONFIG, deviceId)
    },
    async registerDevice(deviceData: RegisterDeviceRequest): Promise<string> {
      const resp = await this.$api.postRegisterDevice(deviceData)
      if (resp.ok) {
        const body = await resp.json()
        const setData: SetDevicePayload = {
          accountId: body.cloud_account_id,
          deviceId: body.cloud_device_id,
          model: body.model,
          submodel: body.subModel,
          serial: body.serial,
          name: body.cloud_state_data?.name?.value as string,
          locationName: body.cloud_state_data?.location_name?.value as string,
          indoor: body.cloud_state_data?.is_indoor?.value as boolean,
          isPublic: body.cloud_state_data?.is_public?.value as boolean,
          coords: {
            lng: (body.cloud_state_data?.location?.value as StateItemPoint)?.coordinates?.[0],
            lat: (body.cloud_state_data?.location?.value as StateItemPoint)?.coordinates?.[1]
          }
        }
        this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.SET_DEVICE}`, setData)
        await this.updateDeviceConfig(body.cloud_device_id)
        return body.cloud_device_id
      }
      const message = await resp.text()
      const formattedError = formatRegisterError(message, deviceData.serial)
      throw new Error(formattedError)
    },
    async updateDevice(
      deviceData: RegisterDeviceRequest,
      deviceId: string
    ): Promise<string | false> {
      let registrationSuccessful = false

      const patchSub = await this.$api.patchDeviceSubscription({
        model: deviceData.model,
        serial: deviceData.serial,
        subscription: this.subscriptionTier,
        isExternal: false
      })
      if (patchSub.ok) {
        // patch successful, update device meta
        const updateDevice = await this.$api.patchUpdateDevice(
          deviceData as UpdateDeviceRequest,
          deviceId
        )
        if (updateDevice.ok) {
          // update successful, registration successful
          registrationSuccessful = true
        } else {
          // update unsuccessful, delete previously patched sub
          await this.$api.patchDeviceSubscription({
            model: deviceData.model,
            serial: deviceData.serial,
            subscription: this.subscriptionTier,
            delete: true,
            isExternal: false
          })
        }
      } else {
        const message = await patchSub.text()
        throw new Error(message)
      }
      if (registrationSuccessful) {
        this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.UPDATE_DEVICE_COORDS}`, {
          deviceId: deviceId,
          coords: {
            lng: deviceData.cloud_state_data.longitude,
            lat: deviceData.cloud_state_data.latitude
          }
        })
        this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.UPDATE_METADATA}`, {
          deviceId: deviceId,
          isPublic: deviceData.cloud_state_data.isPublic,
          indoor: deviceData.cloud_state_data.isIndoor,
          name: deviceData.cloud_state_data.name
        })
        await this.updateDeviceConfig(deviceId)
        return deviceId
      }
      return false
    },
    async save(): Promise<void> {
      this.registering = true
      this.requiredError = false
      this.registerError = false
      this.urlError = ''
      this.urlErrorMsg = ''

      if (!this.fieldsValid) {
        this.requiredError = true
        return
      }

      const deviceData: RegisterDeviceRequest = {
        model: this.modelNumber,
        serial: this.isSmartBridge ? this.idSerialNumber.toLowerCase() : this.idSerialNumber,
        cloud_state_data: {
          isIndoor: this.indoorSensor,
          isPublic: this.isPublic,
          longitude: this.isSmartBridge ? parseFloat('0.0') : parseFloat(this.coords.longitude),
          latitude: this.isSmartBridge ? parseFloat('0.0') : parseFloat(this.coords.latitude),
          name: this.deviceName
        },
        subscription: this.isSmartBridge ? null : this.getDeviceSubscription()
      }

      try {
        let success: string | false = false
        /* Smart Bridge is not associated with Data Services */
        if (this.hasDataServices) {
          success = await this.updateDevice(deviceData, this.devId!)
        } else {
          // saving location name only supported on create NOT update right now
          deviceData.cloud_state_data.locationName = this.deviceLocationName
          success = await this.registerDevice(deviceData)
        }
        if (!success) {
          this.registerError = true
        } else {
          const isManageDevicesRoute =
            this.$router?.currentRoute?.value?.name === MANAGE_DEVICES_ROUTE_NAME
          const isSmartBridge = this.isSmartBridge
          // Set a new device data to Vuex
          if (isManageDevicesRoute) {
            await this.$store.dispatch(
              `${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.SET_ALL_USER_DEVICES}`
            )
            await this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.SET_ALL_DEVICES}`)
          } else {
            await this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.SET_ALL_DEVICES}`)
          }
          // Only update subscriptions and map with non-Smart Bridge device model data
          if (!isSmartBridge) {
            await this.$store.dispatch(
              `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_ACTIONS.UPDATE_SUBSCRIPTIONS}`,
              {
                auth: this.$auth,
                api: this.$api,
                devices: this.$auth.isAuthenticated ? this.$store.state.devices.allDevices : []
              }
            )
            this.updateMap(success, deviceData.model)
          }
          // only redirect to manage devices page if all of this is true
          if (isSmartBridge && !isManageDevicesRoute) {
            this.$router.push({ name: MANAGE_DEVICES_ROUTE_NAME })
          } else {
            // otherwise just close the dialog
            this.close()
          }
        }
      } catch (e) {
        this.registerError = true
        if (e.toString()?.includes(LINK_CUSTOMER)) {
          this.registerErrorMessage = e.toString().replace(LINK_CUSTOMER, '')
          this.urlError = this.sanitizeUrl(APP_ENV.VITE_APP_BASE_URL + '/help?topic=4')
          this.urlErrorMsg = LINK_CUSTOMER
        } else {
          this.registerErrorMessage = e.toString()
        }
      }
      this.registering = false
    },
    close(): void {
      this.init()
      this.$emit('close')
    },
    init(): void {
      this.registerError = false
      this.requiredError = false
      this.deviceName = ''
      this.deviceLocationName = ''
      this.isPublic = false
      this.indoorSensor = false
      this.devicesRemaining = null
      this.deviceType = null
      this.idSerialNumber = ''
      this.coords.latitude = ''
      this.coords.longitude = ''
      this.urlError = ''
      this.urlErrorMsg = ''
      // resets the validation state
      if (this.$refs && this.$refs.form) {
        ;(this.$refs.form as VForm).resetValidation()
      }
    },
    getDeviceSubscription(): string {
      if (!this.showSubTierSelect) {
        return subscriptionTypes.WEBSITE
      }

      return this.subscriptionTier || this.$t('activateDevice.subscriptionTierOptions.premium')
    }
  }
})
</script>

<style lang="scss" scoped>
.v-dialog {
  .v-form {
    margin-left: -16px;
    margin-right: -16px;

    .v-card {
      padding: 0 16px;

      :deep(.v-card-item) {
        padding: 8px 0;
        .v-card-title {
          font-size: 0.875rem;
          font-weight: 400;
          opacity: 0.56;
        }
      }

      .hint-text {
        font-size: 12px;
        opacity: 0.76;
      }
    }
  }
  .v-text-field {
    :deep(.v-messages__message) {
      overflow-wrap: normal;
      hyphens: none;
    }
  }
  .v-input {
    margin-top: 8px;
    margin-bottom: 16px;
  }

  .v-switch {
    position: relative;
    .device-type-indicator {
      align-self: center;
      height: 36px;
      margin: 0 0 0 16px;
      img {
        height: 36px;
      }
    }
  }

  .v-btn.save-button {
    grid-template-columns: 26px 1fr 26px;
  }
}

.move-title {
  font-size: 18px;
  padding: 0.8rem;
  padding-bottom: 0.2rem;
  font-weight: 400;
}
.move-content {
  padding: 0 0.8rem 1.2rem 0.8rem;
  opacity: 0.7;
}
.move-actions {
  padding: 0;
  .v-btn {
    margin: 0 !important;
    padding: 0;
    width: 50%;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    height: 3.2rem;
    font-size: 1rem;
  }
  .v-btn.left {
    border-bottom-right-radius: 0;
  }
  .v-btn.right {
    border-bottom-left-radius: 0;
  }
}
</style>
